import axios, { type AxiosInstance, type AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';
import { useImpersonationStore } from '@/stores/impersonationStore';

// Per spec §16 — versioned base, problem+json errors, idempotency on POSTs.
const baseURL = import.meta.env.VITE_API_BASE ?? '/api/v1';

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Platform admins have no tenant_id of their own — IdentifyTenant on the
  // backend resolves the request's tenant from this header only when the
  // authenticated user has none (see backend/app/Http/Middleware/IdentifyTenant.php).
  // Tenant-scoped users always resolve to their own tenant first, so this
  // header is a no-op for them even if it were somehow present.
  const roles = useAuthStore.getState().user?.roles ?? [];
  const isPlatformAdmin = roles.includes('platform_admin') || roles.includes('super_admin');
  const impersonating = useImpersonationStore.getState().tenant;
  if (isPlatformAdmin && impersonating) {
    config.headers['X-Tenant-Id'] = impersonating.id;
  }

  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err: AxiosError<ProblemDetails | { errors?: Record<string, string[]> }>) => {
    if (err?.response?.status === 401) {
      useAuthStore.getState().clear();
    }
    return Promise.reject(err);
  },
);

// ─── Error utilities ────────────────────────────────────────────────────────

export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  errors?: Record<string, string[]>;
}

/** Extract a human message from a Laravel/RFC-7807 error envelope. */
export function describeError(err: unknown): string {
  const e = err as AxiosError<ProblemDetails>;
  const body = e?.response?.data;
  if (body?.errors) {
    const first = Object.values(body.errors)[0];
    if (first && first.length) return first[0]!;
  }
  if (body?.detail) return body.detail;
  if (body?.title)  return body.title;
  if (e?.message)   return e.message;
  return 'Something went wrong';
}

/** Show an error toast with the best message we can extract. */
export function toastError(err: unknown, fallback = 'Request failed'): void {
  toast.error(describeError(err) || fallback);
}
